// Shared per-part edit-access check for Solution Management delegated admins (P2c).
//
// Mirrors the backend authority model exactly: editService gates the whole request on
// Solution Management > Solution Edit = ALL (the umbrella), then filters per part; a ⊤
// (unscopedAdmin) role bypasses everything. So a control that writes a given solution-part is
// editable only when the caller is ⊤, OR holds Solution Edit=ALL AND that part at ALL.
//
// `metaData` is the redux metaData slice (state.metaData): { unscopedAdmin, appPaths[], partPermissions{},
// modulePermissions{} }.
// `partPermissions` (shipped in P2b) maps partName -> "ALL"/"READ"/absent; "Solution Edit" is a
// page-tab and lives in appPaths, not partPermissions.
import { SETTINGS_PARTS } from "./permissionDependencies";

export const solutionSettingsAll = (metaData) =>
  !!metaData?.unscopedAdmin ||
  (metaData?.appPaths || []).some(
    (p) => p.name === "Solution Edit" && p.permission === "ALL"
  );

// True if the caller may EDIT the given solution part. ⊤ bypasses; otherwise needs the umbrella + part.
// LEGACY-GLOBAL check — still authoritative for "Threshold Management" (a Solution Management
// page-tab, not a Settings-Parts part) and as the fallback for pre-migration role docs.
export const canEditSolutionPart = (metaData, part) =>
  !!metaData?.unscopedAdmin ||
  (solutionSettingsAll(metaData) && metaData?.partPermissions?.[part] === "ALL");

// May the caller EDIT `part` of a solution? Mirrors the backend's heldSolutionParts. The
// editing parts are ROLE-LEVEL: the "Solution Edit" module (read from modulePermissions,
// which the slice builds from the grant-all-expanded modules) applies to every solution the
// role holds, so `serviceId` no longer narrows the answer (kept so the call sites stay
// unchanged). ⊤ bypasses; otherwise the umbrella (Solution Edit page = Full Access) plus the
// part at Full Access. "Threshold Management" stays a global page-tab permission. A role
// with none of the field parts at Partial Access or above has no ceiling and falls back to
// the legacy global partPermissions (Solution Management field parts), as the backend does.
export const canEditPartFor = (metaData, serviceId, part) => {
  if (metaData?.unscopedAdmin) return true;
  if (!solutionSettingsAll(metaData)) return false;
  if (part === "Threshold Management")
    return metaData?.partPermissions?.[part] === "ALL";
  const ceiling = metaData?.modulePermissions?.["Solution Edit"] || {};
  const hasCeiling = SETTINGS_PARTS.some((p) => (PERM_RANK[ceiling[p]] || 0) >= 1);
  if (!hasCeiling) return metaData?.partPermissions?.[part] === "ALL";
  return ceiling[part] === "ALL";
};

// Generic mirror of the BE hasModulePermission(role, "Solution Management", tab, level).
// A Solution-Management tab lives in appPaths (the 3 page-tabs: Solution Creation / Solution
// Settings / Threshold Management) and/or partPermissions (field-parts; "Threshold
// Management" is in BOTH). ⊤ bypasses. level defaults to "ALL". Use this to gate an action
// affordance to the EXACT BE gate it hits (e.g. threshold profile CRUD/apply gate on
// hasSolutionTab(md,"Threshold Management","ALL"); catalogue Duplicate on Solution Creation).
const PERM_RANK = { READ: 1, ALL: 2 };
export const hasSolutionTab = (metaData, tab, level = "ALL") => {
  if (metaData?.unscopedAdmin) return true;
  const need = PERM_RANK[level] || PERM_RANK.ALL;
  const fromAppPaths = (metaData?.appPaths || []).find((p) => p.name === tab)?.permission;
  const fromParts = metaData?.partPermissions?.[tab];
  const have = Math.max(PERM_RANK[fromAppPaths] || 0, PERM_RANK[fromParts] || 0);
  return have >= need;
};

// Per-solution Threshold-Profiles apply/manage check (taxonomy restructure).
// tolerant-absent: while the role carries no TP columns (pre-migration data),
// tpAccess reads "ALL" for held solutions, so nothing narrows until the
// structure migration arms the columns. ⊤ bypasses.
export const tpApplyAllowed = (metaData, serviceId) => {
  if (metaData?.unscopedAdmin) return true;
  return (
    (PERM_RANK[metaData?.tpAccess?.[String(serviceId)]] || 0) >= PERM_RANK.ALL
  );
};
